// PDP Gallery Apparel Overlay — fullscreen rational-images gallery.
// Desktop (>1024px) shows 2 images side-by-side; mobile (≤1024px) shows 1.
(() => {
  const TRANSITION_MS = 500;
  const SNAP_DURATION = 300;
  const DEAD_ZONE     = 10;
  const EASING        = 'cubic-bezier(0.4, 0, 0.2, 1)';

  function lerp(a, b, t) { return a + (b - a) * t; }

  const SLOTS_DESKTOP = {
    left:        { left: '0%',    width: '50%',  opacity: '1', zIndex: '1' },
    right:       { left: '50%',   width: '50%',  opacity: '1', zIndex: '1' },
    'off-left':  { left: '-50%',  width: '50%',  opacity: '0', zIndex: '0' },
    'off-right': { left: '100%',  width: '50%',  opacity: '0', zIndex: '0' },
    hidden:      { left: '150%',  width: '50%',  opacity: '0', zIndex: '0' },
  };

  const SLOTS_MOBILE = {
    center:      { left: '0%',    width: '100%', opacity: '1', zIndex: '1' },
    'off-left':  { left: '-100%', width: '100%', opacity: '0', zIndex: '0' },
    'off-right': { left: '100%',  width: '100%', opacity: '0', zIndex: '0' },
    hidden:      { left: '200%',  width: '100%', opacity: '0', zIndex: '0' },
  };

  class PdpApparelOverlay extends HTMLElement {

    connectedCallback() {
      this.triggerBtn = this.querySelector('.pdp-apparel-overlay__btn');
      this.backdrop   = this.querySelector('.pdp-apparel-overlay__backdrop');
      if (!this.backdrop) return;

      this._allSlides = Array.from(this.backdrop.querySelectorAll('.pdp-apparel-overlay__slide'));
      this._allVideos = Array.from(this.backdrop.querySelectorAll('video'));
      this.slides   = this._allSlides.slice();
      this._videos  = this._allVideos.slice();
      this.prevBtn  = this.backdrop.querySelector('.pdp-apparel-overlay__nav--prev');
      this.nextBtn  = this.backdrop.querySelector('.pdp-apparel-overlay__nav--next');
      this.closeBtn = this.backdrop.querySelector('.pdp-apparel-overlay__close');
      this.stage    = this.backdrop.querySelector('.pdp-apparel-overlay__stage');
      this.pill     = this.backdrop.querySelector('.pdp-apparel-overlay__pill');
      this.pillNav  = this.backdrop.querySelector('.pdp-apparel-overlay__pill-nav');
      this.dragGrip = this.backdrop.querySelector('.pdp-apparel-overlay__drag');
      this.pillSep  = this.backdrop.querySelector('.pdp-apparel-overlay__pill-sep');

      this._isOpen        = false;
      this._currentIdx    = 0;
      this._isAnimating   = false;
      this._dragActive    = false;
      this._dragPos       = 0;
      this._dragHandleDisp = 0;
      this._dragLastFrame  = 0;
      this._dragRAFId     = null;
      this._snapRAFId     = null;
      this._gripStartX    = 0;

      // Move backdrop to <body> to escape stacking contexts.
      this._backdropId = `pdp-apparel-overlay-${Date.now()}`;
      this.backdrop.id = this._backdropId;
      document.body.appendChild(this.backdrop);

      this._centerLine = this.backdrop.querySelector('.pdp-apparel-overlay__center-line');

      if (this.triggerBtn) {
        this._relocateTrigger();
      }

      if (this.slides.length === 0) {
        if (this.triggerBtn) this.triggerBtn.remove();
        this.triggerBtn = null;
        return;
      }

      if (this.triggerBtn) {
        this._addMobileTrigger();
      }

      this._initScrollFade();
      this._bind();
      this._initSlots();
      this._syncControlsVisibility();
    }

    // Filters this.slides to rational slides matching the given color handle.
    // When a color has no matching rational slides the full rational set is kept.
    // Also swaps the trigger thumbnail to the first matching slide.
    syncToColor(handle) {
      if (!handle || !this._allSlides || this._allSlides.length === 0) return;

      const matching = this._allSlides.filter(
        s => s.dataset.rational === 'true' && s.dataset.color === handle
      );
      const active   = matching.length > 0 ? matching : this._allSlides;

      for (const slide of this._allSlides) {
        const isActive = active.includes(slide);
        slide.style.display = isActive ? '' : 'none';
        if (isActive) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      }

      this.slides     = active;
      this._videos    = active.flatMap(s => Array.from(s.querySelectorAll('video')));
      this._currentIdx = 0;

      this._initSlots();
      this._syncControlsVisibility();

      for (const v of this._allVideos) {
        if (!this._videos.includes(v)) v.pause();
      }

      // Re-populate the trigger's 3 static thumb squares from the slides
      // matching the color; falls back to the full set.
      const thumbSrcs = active.map(s => s.dataset.thumbSrc).filter(Boolean).slice(0, 3);
      if (thumbSrcs.length === 0) {
        const defaultSlide =
          this._allSlides.find(s => !s.dataset.color) || this._allSlides[0];
        if (defaultSlide?.dataset.thumbSrc) thumbSrcs.push(defaultSlide.dataset.thumbSrc);
      }

      const targets = [this.triggerBtn, this._mobileTriggerBtn].filter(Boolean);
      for (const btn of targets) {
        const wrap = btn.querySelector('.pdp-apparel-overlay__btn-thumbs');
        if (!wrap) continue;
        for (let i = 0; i < 3; i++) {
          let box = wrap.children[i];
          const src = thumbSrcs[i];
          if (!src) {
            if (box) box.hidden = true;
            continue;
          }
          if (!box) {
            box = document.createElement('span');
            box.className = 'pdp-apparel-overlay__btn-thumb-box';
            const img = document.createElement('img');
            img.className = 'pdp-apparel-overlay__btn-thumb';
            img.alt = '';
            img.loading = 'lazy';
            img.width = 30;
            img.height = 35;
            box.appendChild(img);
            wrap.appendChild(box);
          }
          box.hidden = false;
          const img = box.querySelector('.pdp-apparel-overlay__btn-thumb');
          if (img) {
            img.src = src;
            /* AAS-4874: same contain/cover rule as the Liquid render (keep in
             * sync) — GHO/rational packshots contain, photographic shots cover. */
            img.classList.toggle(
              'pdp-apparel-overlay__btn-thumb--cover',
              !/(_gho_|rational)/i.test(src)
            );
          }
        }
      }
    }

    disconnectedCallback() {
      if (this._keyHandler)    document.removeEventListener('keydown', this._keyHandler);
      if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler);
      if (this._dragMoveBound) {
        document.removeEventListener('mousemove', this._dragMoveBound);
        document.removeEventListener('touchmove', this._dragMoveBound);
        document.removeEventListener('mouseup',   this._dragEndBound);
        document.removeEventListener('touchend',  this._dragEndBound);
      }
      if (this._dragRAFId)  cancelAnimationFrame(this._dragRAFId);
      if (this._snapRAFId)  cancelAnimationFrame(this._snapRAFId);
      const el = document.getElementById(this._backdropId);
      if (el) el.remove();
      if (this._mobileTriggerBtn) this._mobileTriggerBtn.remove();
    }

    _relocateTrigger() {
      const wrapper = this.closest('.product__media-wrapper');
      if (!wrapper) return;

      const viewport = wrapper.querySelector('.pdp-gallery-apparel__viewport');
      if (viewport) { viewport.appendChild(this.triggerBtn); return; }

      const singleImg = wrapper.querySelector('.product__single-image');
      if (singleImg) { singleImg.appendChild(this.triggerBtn); return; }

      // Splide media path: anchor the trigger to the ACTIVE slide (the one
      // matching the selected variant), not blindly the first slide. In an
      // `adapt` gallery the inactive slides collapse to height:0, so anchoring
      // the position:absolute trigger (top:50%) to a non-active slide drops it
      // to the top of the media column, detached from the product — which read
      // as "no gallery button" on any variant whose image isn't the first
      // slide, and caused the trigger to leap on every colour switch. The
      // server marks the variant's slide is-active / data-selected, so this is
      // reliable even before Splide mounts. Fall back to the first slide.
      const targetSlide =
        wrapper.querySelector('.splide__slide.is-active') ||
        wrapper.querySelector('.splide__slide[data-selected="true"]') ||
        wrapper.querySelector('.splide__slide') ||
        wrapper.querySelector('.splide__list > li');
      if (targetSlide) {
        targetSlide.style.position = 'relative';
        targetSlide.appendChild(this.triggerBtn);
      }
    }

    _addMobileTrigger() {
      const wrapper = this.closest('.product__media-wrapper');
      if (!wrapper) return;

      let target = wrapper.querySelector('.apparel-mobile-hero');

      if (!target) {
        const mobileSlide =
          wrapper.querySelector('.large-up-hide .splide__slide.is-active') ||
          wrapper.querySelector('.large-up-hide .splide__slide[data-selected="true"]') ||
          wrapper.querySelector('.large-up-hide .splide__slide') ||
          wrapper.querySelector('.large-up-hide .splide__list > li');
        if (mobileSlide) {
          mobileSlide.style.position = 'relative';
          target = mobileSlide;
        }
      }

      if (!target) return;

      const mobileTrigger = this.triggerBtn.cloneNode(true);
      mobileTrigger.classList.add('pdp-apparel-overlay__btn--mobile');
      mobileTrigger.addEventListener('click', () => this.open());
      target.appendChild(mobileTrigger);
      this._mobileTriggerBtn = mobileTrigger;

      // AAS-5198: the mobile stage image is static (splide drag is off below
      // 1025px, see product-media-gallery.liquid) — tapping the image itself
      // opens the gallery overlay, same as the trigger chip. Bound on the
      // whole mobile stage (the gallery wrap on boots/skis/etc, the hero on
      // apparel) — both are mobile-only containers, so desktop is untouched.
      // The chip's own click bubbles through here too; open()'s re-entry
      // guard absorbs the duplicate. Media that has its own interaction
      // (video, 3D) keeps it — those clicks never open the overlay.
      this._bindOpenSurface(
        wrapper.querySelector('.product-mobile-gallery-wrap') || target,
        'video, model-viewer, deferred-media, product-model'
      );

      // AAS-5198: apparel's inline mobile gallery (the model-shot carousel
      // below the hero) opens the overlay on a tap too. Swipes keep cycling
      // the carousel (drag guard), and its handle-control nav keeps its own
      // interaction.
      this._bindOpenSurface(
        this.closest('.section-main-product')?.querySelector('.apparel-mobile-gallery'),
        'handle-control, button, video, model-viewer, deferred-media, product-model'
      );
    }

    // One tap surface that opens the overlay: pointer-travel guarded (an
    // attempted swipe — old habit on the stage, genuine slide-cycling on the
    // apparel inline gallery — releases as a click too; a finger that
    // travelled must not open a fullscreen overlay; 10px covers touch
    // jitter), with an exclusion list for elements that own their clicks.
    _bindOpenSurface(surface, exclude) {
      if (!surface || surface.dataset.overlayClickBound) return;
      surface.dataset.overlayClickBound = 'true';
      surface.style.cursor = 'pointer';
      let downX = 0;
      let downY = 0;
      surface.addEventListener('pointerdown', (e) => {
        downX = e.clientX;
        downY = e.clientY;
      }, { passive: true });
      surface.addEventListener('click', (e) => {
        if (e.target.closest(exclude)) return;
        if (Math.hypot(e.clientX - downX, e.clientY - downY) > 10) return;
        this.open();
      });
    }

    _initScrollFade() {
      if (!this.triggerBtn && !this._mobileTriggerBtn) return;
      const FADE_RANGE = 200;
      const update = () => {
        const wrapper = this.closest('.product__media-wrapper');
        const ref     = wrapper || this;
        const rect    = ref.getBoundingClientRect();
        const past    = -rect.top;
        const opacity = past <= 0 ? 1 : past >= FADE_RANGE ? 0 : 1 - past / FADE_RANGE;
        const ptr     = opacity === 0 ? 'none' : '';
        // Fade each button layer via the --btn-fade custom property (see CSS),
        // not the button's own opacity: an ancestor with opacity < 1 disables
        // the blur layer's backdrop-filter, so the frosted glass would pop out
        // abruptly instead of fading with the thumbs and label. At rest
        // (opacity 1) remove the property so no layer is promoted to a compositor
        // layer, keeping the label crisp under its fractional-pixel centering transform.
        const applyFade = (btn) => {
          if (!btn) return;
          if (opacity === 1) btn.style.removeProperty('--btn-fade');
          else               btn.style.setProperty('--btn-fade', String(opacity));
          btn.style.pointerEvents = ptr;
        };
        applyFade(this.triggerBtn);
        applyFade(this._mobileTriggerBtn);
      };
      this._scrollHandler = update;
      window.addEventListener('scroll', this._scrollHandler, { passive: true });
      update();
    }

    get _isMobile() { return window.innerWidth <= 1024; }
    get _perPage()  { return this._isMobile ? 1 : 2; }

    _wrap(i) {
      const len = this.slides.length;
      return ((i % len) + len) % len;
    }

    _syncControlsVisibility() {
      if (this._isMobile) {
        for (const el of [this.prevBtn, this.nextBtn, this.dragGrip]) {
          if (el) el.classList.add('is-hidden');
        }
        if (this.pillNav) this.pillNav.classList.add('is-hidden');
        if (this.pillSep) this.pillSep.classList.add('is-hidden');
        return;
      }
      const hasNav = this.slides.length > this._perPage;
      for (const el of [this.prevBtn, this.nextBtn, this.dragGrip]) {
        if (el) el.classList.toggle('is-hidden', !hasNav);
      }
      if (this.pillNav) this.pillNav.classList.toggle('is-hidden', !hasNav);
      if (this.pillSep) this.pillSep.classList.toggle('is-hidden', !hasNav);
    }

    _slotProps(slotName) {
      const table = this._isMobile ? SLOTS_MOBILE : SLOTS_DESKTOP;
      return table[slotName] ?? (this._isMobile ? SLOTS_MOBILE.hidden : SLOTS_DESKTOP.hidden);
    }

    _applySlot(slide, slotName, animate) {
      const s = this._slotProps(slotName);
      slide.style.position   = 'absolute';
      slide.style.top        = '0';
      slide.style.height     = '100%';
      slide.style.left       = s.left;
      slide.style.width      = s.width;
      slide.style.opacity    = s.opacity;
      slide.style.zIndex     = s.zIndex;
      slide.style.transition = animate
        ? `left ${TRANSITION_MS}ms ${EASING}, opacity ${TRANSITION_MS}ms ease`
        : 'none';
    }

    _applyMobileFlow() {
      const props = ['position', 'top', 'left', 'width', 'height',
                     'opacity', 'zIndex', 'transition', 'display'];
      for (const slide of this.slides) {
        for (const prop of props) slide.style[prop] = '';
      }
      if (this.stage) this.stage.scrollTop = 0;
    }

    _initSlots() {
      const len    = this.slides.length;
      const mobile = this._isMobile;

      if (mobile) {
        this._applyMobileFlow();
        return;
      }

      for (const slide of this.slides) {
        this._applySlot(slide, 'hidden', false);
      }
      if (len === 0) return;

      if (len === 1) {
        Object.assign(this.slides[0].style, {
          left: '0%', width: '100%', opacity: '1', zIndex: '1'
        });
      } else {
        this._applySlot(this.slides[this._currentIdx],                 'left',  false);
        this._applySlot(this.slides[this._wrap(this._currentIdx + 1)], 'right', false);
      }
    }

    _navigateNext() {
      if (this._isMobile || this._isAnimating || this._dragActive || this.slides.length <= this._perPage) return;
      this._isAnimating = true;

      const oldLeft  = this._currentIdx;
      const oldRight = this._wrap(this._currentIdx + 1);
      const incoming = this._wrap(this._currentIdx + 2);

      this.slides[incoming].style.transition = 'none';
      this._applySlot(this.slides[incoming], 'off-right', false);
      this.slides[incoming].offsetHeight;

      this._applySlot(this.slides[oldLeft],  'off-left', true);
      this._applySlot(this.slides[oldRight], 'left',     true);
      this._applySlot(this.slides[incoming], 'right',    true);
      this._currentIdx = oldRight;

      setTimeout(() => { this._isAnimating = false; }, TRANSITION_MS + 50);
    }

    _navigatePrev() {
      if (this._isMobile || this._isAnimating || this._dragActive || this.slides.length <= this._perPage) return;
      this._isAnimating = true;

      const oldLeft  = this._currentIdx;
      const oldRight = this._wrap(this._currentIdx + 1);
      const incoming = this._wrap(this._currentIdx - 1);

      this.slides[incoming].style.transition = 'none';
      this._applySlot(this.slides[incoming], 'off-left', false);
      this.slides[incoming].offsetHeight;

      this._applySlot(this.slides[oldRight], 'off-right', true);
      this._applySlot(this.slides[oldLeft],  'right',     true);
      this._applySlot(this.slides[incoming], 'left',      true);
      this._currentIdx = incoming;

      setTimeout(() => { this._isAnimating = false; }, TRANSITION_MS + 50);
    }

    // Continuous velocity-based drag. _dragPos is a float in [0, len);
    // _renderDragPosition lerps between integer states (3 visible on desktop, 2 on mobile).
    _startDrag() {
      if (this._isMobile || this._dragActive || this._isAnimating || this.slides.length <= this._perPage) return;
      this._dragActive      = true;
      this._dragPos         = this._currentIdx;
      this._dragHandleDisp  = 0;
      this._dragLastFrame   = performance.now();

      if (this._snapRAFId) { cancelAnimationFrame(this._snapRAFId); this._snapRAFId = null; }

      for (const slide of this.slides) slide.style.transition = 'none';

      this._renderDragPosition(this._dragPos);

      const loop = (now) => {
        if (!this._dragActive) return;
        const dt  = Math.min(0.1, (now - this._dragLastFrame) / 1000);
        this._dragLastFrame = now;

        const d   = this._dragHandleDisp;
        const abs = Math.abs(d) - DEAD_ZONE;
        if (abs > 0) {
          const dir   = d > 0 ? 1 : -1;
          const speed = abs * abs * 0.00025;
          const len   = this.slides.length;
          this._dragPos = ((this._dragPos + dir * speed * dt) % len + len) % len;
        }

        this._renderDragPosition(this._dragPos);
        this._dragRAFId = requestAnimationFrame(loop);
      };
      this._dragRAFId = requestAnimationFrame(loop);
    }

    _renderDragPosition(pos) {
      const len    = this.slides.length;
      const mobile = this._isMobile;
      const rawI   = Math.floor(pos);
      const f      = pos - rawI;

      for (const s of this.slides) s.style.display = 'none';

      const wi0 = ((rawI    ) % len + len) % len;
      const wi1 = ((rawI + 1) % len + len) % len;

      if (mobile) {
        const s0 = this.slides[wi0];
        const s1 = this.slides[wi1];

        s0.style.display = '';
        s0.style.left    = `${lerp(0, -100, f)}%`;
        s0.style.width   = '100%';
        s0.style.opacity = '1';
        s0.style.zIndex  = '1';

        if (s1 !== s0) {
          s1.style.display = '';
          s1.style.left    = `${lerp(100, 0, f)}%`;
          s1.style.width   = '100%';
          s1.style.opacity = '1';
          s1.style.zIndex  = '1';
        }
      } else {
        const wi2 = ((rawI + 2) % len + len) % len;
        const s0  = this.slides[wi0];
        const s1  = this.slides[wi1];
        const s2  = this.slides[wi2];

        s0.style.display = '';
        s0.style.left    = `${lerp(0, -50, f)}%`;
        s0.style.width   = '50%';
        s0.style.opacity = '1';
        s0.style.zIndex  = '1';

        if (s1 !== s0) {
          s1.style.display = '';
          s1.style.left    = `${lerp(50, 0, f)}%`;
          s1.style.width   = '50%';
          s1.style.opacity = '1';
          s1.style.zIndex  = '2';
        }

        if (s2 !== s0 && s2 !== s1) {
          s2.style.display = '';
          s2.style.left    = `${lerp(100, 50, f)}%`;
          s2.style.width   = '50%';
          s2.style.opacity = '1';
          s2.style.zIndex  = '1';
        }
      }
    }

    _stopDrag() {
      if (!this._dragActive) return;
      this._dragActive = false;
      if (this._dragRAFId) { cancelAnimationFrame(this._dragRAFId); this._dragRAFId = null; }
      this._snapDragToNearest();
    }

    _snapDragToNearest() {
      const len = this.slides.length;
      const pos = ((this._dragPos % len) + len) % len;
      let target = Math.round(pos);
      if (target >= len) target = 0;

      let diff = target - pos;
      if (diff >  len / 2) diff -= len;
      if (diff < -len / 2) diff += len;

      const start = pos;
      const end   = pos + diff;
      const t0    = performance.now();
      this._isAnimating = true;

      const frame = (now) => {
        const t    = Math.min(1, (now - t0) / SNAP_DURATION);
        const ease = 1 - Math.pow(1 - t, 3);
        const p    = (((start + (end - start) * ease) % len) + len) % len;

        this._renderDragPosition(p);

        if (t < 1) {
          this._snapRAFId = requestAnimationFrame(frame);
        } else {
          this._currentIdx = ((target % len) + len) % len;

          for (const s of this.slides) {
            s.style.display    = '';
            s.style.transition = 'none';
          }
          this._initSlots();
          this.slides[0]?.offsetHeight;

          this._snapRAFId   = null;
          this._isAnimating = false;
        }
      };
      this._snapRAFId = requestAnimationFrame(frame);
    }

    _bindDragGrip() {
      if (!this.dragGrip) return;

      const onStart = (e) => {
        e.preventDefault();
        this._gripStartX     = e.touches ? e.touches[0].clientX : e.clientX;
        this._dragHandleDisp = 0;
        this.dragGrip.classList.add('is-dragging');
        if (this.pill) this.pill.classList.remove('is-returning');
        this._startDrag();
      };

      this._dragMoveBound = (e) => {
        if (!this._dragActive) return;
        const clientX = e.touches
          ? (e.touches.length > 0 ? e.touches[0].clientX : null)
          : e.clientX;
        if (clientX == null) return;
        this._dragHandleDisp = clientX - this._gripStartX;
        if (this.pill) {
          this.pill.style.setProperty('--pill-offset', `${this._dragHandleDisp}px`);
        }
      };

      this._dragEndBound = () => {
        if (!this._dragActive) return;
        this.dragGrip.classList.remove('is-dragging');
        if (this.pill) {
          this.pill.classList.add('is-returning');
          this.pill.style.setProperty('--pill-offset', '0px');
          this.pill.addEventListener('transitionend', () => {
            this.pill.classList.remove('is-returning');
          }, { once: true });
        }
        this._dragHandleDisp = 0;
        this._stopDrag();
      };

      this.dragGrip.addEventListener('mousedown',  onStart);
      this.dragGrip.addEventListener('touchstart', onStart, { passive: false });
      document.addEventListener('mousemove',  this._dragMoveBound);
      document.addEventListener('touchmove',  this._dragMoveBound, { passive: true });
      document.addEventListener('mouseup',    this._dragEndBound);
      document.addEventListener('touchend',   this._dragEndBound);
    }

    /* The image the shopper is looking at in the PDP gallery — the flight's
       starting frame. The trigger button is appended to that same slide (see
       _addTrigger / _addMobileTrigger), so the selectors mirror the ones used
       there; falling back to the first slide keeps them in step. */
    _flipSourceImage() {
      const wrapper = this.closest('.product__media-wrapper');
      if (!wrapper) return null;
      const selectors = this._isMobile
        ? ['.apparel-mobile-hero img',
           '.large-up-hide .splide__slide.is-active img',
           '.large-up-hide .splide__slide[data-selected="true"] img',
           '.large-up-hide .splide__slide img']
        : [/* Apparel/ski desktop lead with the scroll-morph hero rather than a
              splide slide, so it comes first; the splide selectors cover the
              templates that do carousel there. */
           '.product__single-image-img',
           '.splide__slide.is-active img',
           '.splide__slide[data-selected="true"] img',
           '.splide__slide img'];
      for (const sel of selectors) {
        const img = wrapper.querySelector(sel);
        if (img) {
          const r = img.getBoundingClientRect();
          if (r.width > 1 && r.height > 1) return img;
        }
      }
      return null;
    }

    /* Immersive open (AAS-4279): instead of the overlay simply fading up, the
       current image flies from its place in the PDP to the slot it occupies in
       the overlay, while the backdrop fades everything else away behind it.

       FLIP, with a fixed-position clone rather than the element itself: the
       source stays in the page (so closing leaves the PDP untouched) and the
       clone can sit above the backdrop during the flight.

       Transform-only, so the whole flight runs on the compositor: animating
       top/left/width/height instead re-laid-out the clone every frame, which is
       what made it stutter.

       The catch transform normally brings is distortion — the two BOXES differ
       in aspect ratio (the PDP frame is near-square, the overlay slot is tall),
       so mapping one onto the other needs a non-uniform scale. The way out is
       to map the rendered PHOTO rects rather than the boxes: both letterbox the
       same image with object-fit, so the painted rect has an identical aspect
       ratio at both ends and the scale comes out uniform. _paintedRect() works
       that rect out from the natural dimensions.

       Bails out silently (leaving the plain fade) when reduced motion is asked
       for, or when either box cannot be measured. */

    /* Where the image is actually painted inside its box. With object-fit:
       contain the element is letterboxed, so the box is not what the eye
       follows — flying box-to-box makes the photo appear to drift and jump at
       the hand-off. `cover` fills the box (cropped), so the box IS the painted
       rect there. */
    _paintedRect(img, box) {
      const fit = getComputedStyle(img).objectFit;
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      if (fit !== 'contain' || !nw || !nh) return box;
      const scale = Math.min(box.width / nw, box.height / nh);
      const w = nw * scale;
      const h = nh * scale;
      return {
        left: box.left + (box.width - w) / 2,
        top: box.top + (box.height - h) / 2,
        width: w,
        height: h,
      };
    }
    /* The flight only reads as a zoom when the picture the clone lands on is the
       picture it took off from. Apparel and skis open the overlay on the same
       image the PDP is showing, so they morph cleanly. Boots lead the gallery
       with a different first shot, which breaks it two ways: the clone visibly
       turns into another photo mid-flight, and the uniform scale below — derived
       from width alone — assumes ONE aspect ratio at both ends, so a
       differently-shaped photo lands stretched.

       When the two ends don't match we bail out, which leaves the plain fade —
       the same graceful path reduced-motion and unmeasurable boxes already take.

       Deliberately conservative: a check only vetoes when it can prove a
       mismatch, so an unreadable src or an unloaded image keeps today's
       behaviour rather than silently dropping the animation. */
    _flipImagesMatch(source, target) {
      if (!source || !target) return false;

      // Same asset? Both ends request different widths of the same master, so
      // compare the path with the sizing query stripped.
      const assetKey = (img) => {
        const raw = img.currentSrc || img.src || '';
        if (!raw) return '';
        try {
          return new URL(raw, window.location.href).pathname;
        } catch (e) {
          return raw.split('?')[0];
        }
      };
      const keyA = assetKey(source);
      const keyB = assetKey(target);
      if (keyA && keyB && keyA !== keyB) return false;

      // Same shape? Guards the uniform-scale maths directly, and still applies
      // when the URLs aren't comparable (e.g. one end served from another host).
      const ratio = (img) =>
        img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0;
      const arA = ratio(source);
      const arB = ratio(target);
      if (arA && arB && Math.abs(arA - arB) / Math.max(arA, arB) > 0.02) return false;

      return true;
    }

    _runOpenTransition(ready) {
      if (!ready) return;
      const { from, source, target } = ready;

      // Liftoff: create the clone at the hero's painted rect and fly it into
      // the overlay slot. Until this runs there is NO clone — on the
      // staggered mobile-ski path the real in-flow hero stays visible and
      // the departing panel physically uncovers it, which is what makes the
      // choreography read as "opening the image from underneath". A clone
      // painted at tap-time sat ABOVE the panel and showed the covered part
      // of the packshot instantly, breaking that reveal.
      const liftoff = () => {
        this._flipLand = null;
        const to = target.getBoundingClientRect();
        if (to.width < 1 || to.height < 1) return;

        const clone = (source || target).cloneNode(true);
        clone.removeAttribute('id');
        clone.className = 'pdp-apparel-overlay__flip';

        // Painted rects, not boxes — see the note above.
        const fromPainted = source ? this._paintedRect(source, from) : from;
        const toPainted = this._paintedRect(target, to);
        if (toPainted.width < 1 || toPainted.height < 1) return;

        // The clone is laid out ONCE, at the destination. Everything after this
        // is transform, so no further layout happens during the flight.
        Object.assign(clone.style, {
          position: 'fixed',
          top: `${toPainted.top}px`,
          left: `${toPainted.left}px`,
          width: `${toPainted.width}px`,
          height: `${toPainted.height}px`,
          margin: '0',
          objectFit: 'fill', // the box IS the painted rect now, so no letterbox
          /* Above the backdrop (1000) so the flight is visible over it, and
             above the header, which sits at 900 while the overlay is open. */
          zIndex: '1002',
          pointerEvents: 'none',
          transition: 'none',
          transformOrigin: 'top left',
          willChange: 'transform',
          backfaceVisibility: 'hidden', // keep it on its own layer
        });

        // Start frame: the destination box mapped back onto the source. Uniform
        // scale, because both painted rects share the photo's aspect ratio.
        const scale = fromPainted.width / toPainted.width;
        const dx = fromPainted.left - toPainted.left;
        const dy = fromPainted.top - toPainted.top;
        clone.style.transform = `translate3d(${dx}px, ${dy}px, 0) scale(${scale})`;
        document.body.appendChild(clone);

        // Hide BOTH originals so the clone is the only copy of the image on
        // screen. The target is obvious; the source matters just as much,
        // because the backdrop fades in over ~500ms and the PDP is visible
        // through it for that whole time — leaving the source in place read as
        // a ghost of the old image dissolving while the clone flew over it.
        // Restored in close(), not on landing: while the overlay is open the
        // source is behind an opaque backdrop, so it can stay hidden.
        target.style.opacity = '0';
        if (source) {
          this._flipSource = source;
          this._flipSourceVisibility = source.style.visibility;
          source.style.visibility = 'hidden';
        }
        void clone.offsetWidth; // commit the start frame before transitioning

        clone.style.transition =
          'transform var(--duration-long, 500ms) cubic-bezier(0.4, 0, 0.2, 1)';
        clone.style.transform = 'translate3d(0, 0, 0) scale(1)';

        // Land once, whichever comes first: the last property to finish, or a
        // safety timeout for the case where transitionend never fires (the
        // overlay closed mid-flight, the tab was backgrounded).
        let landed = false;
        const land = () => {
          if (landed) return;
          landed = true;
          clearTimeout(timer);
          clone.removeEventListener('transitionend', land);
          target.style.opacity = '';
          clone.remove();
          this._flipLand = null;
        };
        const timer = setTimeout(land, 1200);
        clone.addEventListener('transitionend', land);
        this._flipLand = land;
      };

      // AAS-5075: mobile ski staggers the choreography — the pinned panel
      // slides away first (its transition started with the body flag in
      // open()), the real hero is revealed underneath, and the flight lifts
      // off once the slide is ~60% done (300ms into its 500ms run). The
      // backdrop's fade-in is CSS-delayed by the same 300ms under the body
      // flag so the reveal happens in the clear. Everywhere else liftoff is
      // immediate, with the fade, as before.
      const staggered =
        this._isMobile && document.body.classList.contains('template-product-ski');
      if (staggered) {
        const pending = setTimeout(liftoff, 300);
        // close() before liftoff lands the "flight" by cancelling it — the
        // flag itself is cleared in close().
        this._flipLand = () => {
          clearTimeout(pending);
          this._flipLand = null;
        };
      } else {
        requestAnimationFrame(liftoff);
      }
    }

    open() {
      if (this._isOpen) return;
      this._isOpen     = true;
      this._currentIdx = 0;
      // Measure the source BEFORE the overlay paints over it.
      //
      // Apparel keeps the original plain fade. Its scroll-triptych gallery owns
      // the hero itself — it is fixed, scroll-driven and mid-morph at the moment
      // the overlay opens — so a flight measured against it fought the gallery's
      // own animation instead of complementing it. The other templates hand the
      // overlay a settled image, which is what the flight needs. AAS-4279
      const isApparel =
        document.body.classList.contains('template-product-apparel') ||
        !!document.querySelector('.product--scroll_triptych');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let flipFrom = null;
      if (!reduceMotion && !isApparel) {
        const sourceImg = this._flipSourceImage();
        if (sourceImg) flipFrom = sourceImg.getBoundingClientRect();
      }
      this._initSlots();
      this._syncControlsVisibility();
      // AAS-5075: decide the zoom-vs-fade NOW, before the backdrop's opacity
      // transition starts — the body flag must already be in place when the
      // transition begins (a) for the pinned ski panel to start its slide on
      // this same frame and (b) because transition-delay (the ski-mobile
      // backdrop hold, see CSS) only applies to transitions that START while
      // the rule matches; running transitions ignore later changes.
      let flipReady = null;
      if (flipFrom) {
        const firstSlide = this.slides && this.slides[0];
        const target = firstSlide ? firstSlide.querySelector('img') : null;
        const source = this._flipSourceImage();
        // Different picture at the far end → plain fade instead of a broken zoom.
        if (target && this._flipImagesMatch(source, target)) {
          flipReady = { from: flipFrom, source, target };
          document.body.classList.add('pdp-apparel-overlay-flip-open');
        }
      }
      this.backdrop.classList.add('is-open');
      this._runOpenTransition(flipReady);
      for (const v of this._videos) v.play().catch(() => {});
      if (this._isMobile) {
        if (this.stage) this.stage.scrollTop = 0;
      } else if (this._centerLine && this.slides.length >= 2) {
        this._centerLine.classList.add('is-open');
      }
      // iOS: freeze body position to prevent momentum scroll behind the overlay.
      this._scrollY = window.scrollY;
      document.body.classList.add('pdp-apparel-overlay-lock');
      window.ScrollLock && ScrollLock.lock('apparel-lightbox');
      document.body.style.top = `-${this._scrollY}px`;
      requestAnimationFrame(() => { if (this.closeBtn) this.closeBtn.focus(); });
    }

    close() {
      if (!this._isOpen) return;
      this._isOpen = false;
      // Land any in-flight open transition first, or its clone would outlive
      // the overlay and the real image would stay hidden on the next open.
      if (this._flipLand) this._flipLand();
      // Give the PDP its image back — it was hidden so it could not ghost
      // behind the fading backdrop while the clone flew.
      if (this._flipSource) {
        this._flipSource.style.visibility = this._flipSourceVisibility || '';
        this._flipSource = null;
      }
      this.backdrop.classList.remove('is-open');
      // AAS-5075: bring the mobile ski panel back as the backdrop fades.
      document.body.classList.remove('pdp-apparel-overlay-flip-open');
      for (const v of this._videos) v.pause();
      if (this._centerLine) this._centerLine.classList.remove('is-open');
      document.body.classList.remove('pdp-apparel-overlay-lock');
      window.ScrollLock && ScrollLock.unlock('apparel-lightbox');
      document.body.style.top = '';
      window.scrollTo(0, this._scrollY || 0);
      if (this.triggerBtn) this.triggerBtn.focus();
    }

    _bind() {
      if (this.triggerBtn) {
        this.triggerBtn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      }
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
      if (this.prevBtn)  this.prevBtn.addEventListener('click',  () => this._navigatePrev());
      if (this.nextBtn)  this.nextBtn.addEventListener('click',  () => this._navigateNext());

      this._keyHandler = (e) => {
        if (!this._isOpen) return;
        if (e.key === 'Escape')     { e.preventDefault(); this.close(); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this._navigatePrev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this._navigateNext(); }
      };
      document.addEventListener('keydown', this._keyHandler);

      // Touch swipe on the stage (desktop-layout mode only — mobile uses native scroll).
      let swipeX = 0, swipeY = 0, swiping = false;
      if (this.stage) {
        this.stage.addEventListener('touchstart', (e) => {
          if (this._isMobile || e.touches.length !== 1) return;
          swipeX = e.touches[0].clientX;
          swipeY = e.touches[0].clientY;
          swiping = true;
        }, { passive: true });

        this.stage.addEventListener('touchmove', (e) => {
          if (this._isMobile || !swiping || e.touches.length !== 1) return;
          const dy = e.touches[0].clientY - swipeY;
          const dx = e.touches[0].clientX - swipeX;
          if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) swiping = false;
        }, { passive: true });

        this.stage.addEventListener('touchend', (e) => {
          if (this._isMobile || !swiping) return;
          swiping = false;
          const dx = (e.changedTouches[0]?.clientX ?? swipeX) - swipeX;
          if (Math.abs(dx) > 40) {
            if (dx < 0) this._navigateNext();
            else        this._navigatePrev();
          }
        }, { passive: true });
      }

      this._bindDragGrip();

      window.addEventListener('resize', () => {
        this._syncControlsVisibility();
        if (!this._isOpen || this._dragActive) return;
        this._initSlots();
        if (this._centerLine) {
          this._centerLine.classList.toggle('is-open', !this._isMobile && this.slides.length >= 2);
        }
      }, { passive: true });
    }
  }

  if (!customElements.get('pdp-apparel-overlay')) {
    customElements.define('pdp-apparel-overlay', PdpApparelOverlay);
  }
})();
